import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FileError } from 'react-dropzone';
import {
  Box,
  FlexListPlaceholder,
  List,
  PaginationBar,
  SearchInput,
  Text,
  useDebounce,
  usePaginationParams,
  useResponsive,
} from '@common-fe/common-fe';

import { ROUTES } from '@/common';
import UploadFileErrorPopup from '@/components/elements/DropScan/UploadFileErrorPopup';
import { useHistory } from '@/modules/core';
import { useGeneralInfoStore } from '@/modules/employee/Employee/PersonalInformation/GeneralInfo/stores';
import { useFilters } from '@/utils';

import { useLMNList } from '../hooks';
import { LMN_UPLOAD_STORE_KEY, LMNPopupMode } from '../LettersOfMedicalNecessity.types';
import LettersOfMedicalNecessityPopup from '../LettersOfMedicalNecessityPopup';
import UploadButton from '../UploadButton';

import LettersOfMedicalNecessityDeleteModal from './LettersOfMedicalNecessityDeleteModal';
import LettersOfMedicalNecessityListItem from './LettersOfMedicalNecessityListItem';

const DEFAULT_PAGE_SIZE = 10;
const SEARCH_CHARS_COUNT = 3;
interface Props {}
const LettersOfMedicalNecessityList: React.FC<Props> = () => {
  const { isMobile } = useResponsive();
  const history = useHistory();

  const { employeeId } = useGeneralInfoStore((state) => state.state);
  const [isAddModalVisible, setAddModalVisible] = useState(false);
  const [uploadedHighlight, setUploadedHighlight] = useState<string>('');
  const [isFileUploadErrorModalVisible, setIsFileUploadErrorModalVisible] = useState(false);
  const { page, perPage, setPerPage, setPage, searchString } = usePaginationParams(DEFAULT_PAGE_SIZE);
  const [fileUploadError, setFileUploadError] = useState<FileError | undefined>();
  const [deleteLetterId, setDeleteLetterId] = useState<string | undefined>(undefined);
  const [modalMode, setModalMode] = useState<LMNPopupMode>(LMNPopupMode.VIEW);
  const { filtersValues, setFilterValue } = useFilters<{ searchString: string }>(
    { searchString },
    { autoClear: true }
  );
  const debouncedSearch = useDebounce(
    filtersValues.searchString.length >= SEARCH_CHARS_COUNT ? filtersValues.searchString : ''
  );
  const { isLoading, lettersOfMedicalNecessity, total } = useLMNList({
    page,
    perPage,
    searchString: debouncedSearch,
  });
  const [viewLmnItemId, setViewLmnItemId] = useState<string | undefined>(undefined);

  const handleDelete = useCallback((id?: string) => {
    setDeleteLetterId(id);
  }, []);

  const handleView = useCallback(
    (id?: string) => {
      if (isMobile) {
        history.push(ROUTES.MY_DOCUMENTS_LMN_VIEW_MOBILE(id));
      } else {
        setViewLmnItemId(id);
        setModalMode(LMNPopupMode.VIEW);
        setIsViewLmnPopupVisible(true);
      }
    },
    [isMobile, history]
  );
  const deletedItem = useMemo(() => {
    return lettersOfMedicalNecessity?.find((letter) => letter.id === deleteLetterId);
  }, [lettersOfMedicalNecessity, deleteLetterId]);
  const onFileUploadError = useCallback((error?: FileError) => {
    setFileUploadError(error);
    setIsFileUploadErrorModalVisible(true);
  }, []);
  const handleEdit = useCallback(
    (id?: string) => {
      if (isMobile) {
        history.push(ROUTES.MY_DOCUMENTS_LMN_EDIT_MOBILE(id));
      } else {
        setViewLmnItemId(id);
        setModalMode(LMNPopupMode.EDIT);
        setIsViewLmnPopupVisible(true);
      }
    },
    [isMobile, history]
  );
  const [isViewLmnPopupVisible, setIsViewLmnPopupVisible] = useState(false);
  const onCloseFileUploadError = useCallback(() => {
    setFileUploadError(undefined);
    setIsFileUploadErrorModalVisible(false);
  }, []);
  const children = useMemo(() => {
    if (isLoading) {
      return <List.Preloader testId="lmn-list_preloader" />;
    }

    if (!lettersOfMedicalNecessity?.length) {
      return (
        <List.Placeholder testId="lmn-list_placeholder">
          <FlexListPlaceholder />
        </List.Placeholder>
      );
    }

    return lettersOfMedicalNecessity?.map((letterOfMedicalNecessity) => (
      <LettersOfMedicalNecessityListItem
        isHighlighted={uploadedHighlight === letterOfMedicalNecessity?.id}
        key={letterOfMedicalNecessity?.id}
        id={letterOfMedicalNecessity?.id}
        name={letterOfMedicalNecessity?.name}
        description={letterOfMedicalNecessity?.description}
        claims={letterOfMedicalNecessity?.claims}
        validFrom={letterOfMedicalNecessity?.validFrom}
        validTo={letterOfMedicalNecessity?.validTo}
        fileUrl={letterOfMedicalNecessity?.thumbnailUrl}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
    ));
  }, [
    isLoading,
    lettersOfMedicalNecessity,
    uploadedHighlight,
    handleView,
    handleEdit,
    handleDelete,
  ]);
  const handleEditFromView = useCallback(() => {
    setModalMode(LMNPopupMode.EDIT);
  }, []);
  const handleUpload = useCallback(() => {
    setAddModalVisible(true);
  }, []);
  useEffect(() => {
    setPage(1);
  }, [setPage, filtersValues.searchString]);

  return (
    <Box data-testId="lmn-list_wrapper" pad="spacing24" background="module" round="moduleRound">
      <Box direction="row" align="center" justify="between">
        <Text weight={700} data-testId="lmn-list_total">
          Files: {total}
        </Text>

        <Box gap="spacing12" direction="row" align="center">
          <Box width={{ min: '305px' }}>
            <SearchInput
              testId="lmn-list"
              value={filtersValues.searchString}
              onChange={setFilterValue.searchString}
              placeholder="Search for file name or description"
              hasSearchingHistory
            />
          </Box>

          <Box direction="row" align="center">
            <UploadButton
              onUpload={handleUpload}
              onFileUploadError={onFileUploadError}
              style={{ minWidth: '120px' }}
              storeKey={LMN_UPLOAD_STORE_KEY}
            />
          </Box>
        </Box>
      </Box>

      <Box margin={{ top: 'spacing24' }}>
        <List>
          <List.Header>
            <List.Header.Item flex={0.6}></List.Header.Item>
            <List.Header.Item flex={1}>File name</List.Header.Item>
            <List.Header.Item flex={1.8}>Description</List.Header.Item>
            <List.Header.Item flex={1}>Linked claims</List.Header.Item>
            <List.Header.Item flex={1}>Dates</List.Header.Item>
            <List.Header.Item flex={0.5} textAlign="end">
              Actions
            </List.Header.Item>
          </List.Header>

          {children}
        </List>
      </Box>
      <LettersOfMedicalNecessityDeleteModal deletedItem={deletedItem} onSetId={setDeleteLetterId} />
      {!!lettersOfMedicalNecessity?.length && (
        <PaginationBar
          total={total}
          page={page}
          onChangePage={setPage}
          pageSize={perPage}
          onChangePageSize={setPerPage}
        />
      )}

      {isAddModalVisible && (
        <LettersOfMedicalNecessityPopup
          mode={LMNPopupMode.UPLOAD}
          onSuccess={setUploadedHighlight}
          onSetVisible={setAddModalVisible}
          employeeId={employeeId}
        />
      )}
      {isFileUploadErrorModalVisible && (
        <UploadFileErrorPopup fileError={fileUploadError} onClose={onCloseFileUploadError} />
      )}
      {isViewLmnPopupVisible ? (
        <LettersOfMedicalNecessityPopup
          itemId={viewLmnItemId}
          onSetVisible={setIsViewLmnPopupVisible}
          mode={modalMode || LMNPopupMode.VIEW}
          onEdit={handleEditFromView}
          employeeId={employeeId}
          onDelete={handleDelete}
        />
      ) : null}
    </Box>
  );
};

export default LettersOfMedicalNecessityList;
